import { defaultEmailStyleString, genInternalId, TokenPrefix } from "@sp-crm/core";
import { InlineBanner } from "components/ui/inline-banner";
import { AdvancedSearchEntityType, useRelocateImageMutation } from "generated/graphql";
import React, {
    forwardRef,
    ForwardRefExoticComponent,
    RefAttributes,
    useImperativeHandle,
} from "react";
import {
    AutoFormatPlugin,
    ContentModelFormatter,
    createEntity,
    EditorPlugin,
    EditPlugin,
    HyperlinkPlugin,
    insertEntity,
    PastePlugin,
    PluginEvent,
    ShortcutPlugin,
} from "roosterjs";
import { EditorAdapter, EditorAdapterOptions } from "roosterjs-editor-adapter";
import { ResponsiveMode } from "store/reducers/responsive";
import { useResponsiveMode } from "store/selectors/hooks";
import { breakNewlines } from "util/text";
import { FormattingButtons } from "./formatting-buttons";
import { PlaceholderPopover } from "./placeholder-popover";
import { Ribbon } from "./ribbon";
import { removeHtmlIds } from "./sanitize-html-ids";
import { SignatureMenu } from "./signature-menu";
import { Template } from "./template";
import { TemplateMenu } from "./template-menu";

interface Props {
    initialContent?: string;
    templates?: Template[];
    signatures?: Template[];
    onTemplateInserted?: (template: Template) => void;
    tokenReplacements?: Record<string, string>;
    onTokenInserted?: (tokenValue: string) => boolean;
    placeholderEntity?: unknown | null;
    onBlur?: () => void;
    placeholderTypes?: AdvancedSearchEntityType[];
    preserveTokens?: TokenPrefix[];
    onEntityAdded?: (entity: EntityInfo) => void;
    onEntityRemoved?: (entity: EntityInfo) => void;
}

const useForceUpdate = () => {
    const s = React.useState(0);
    return () => s[1](value => value + 1); // update the state to force render
};

const useUnmount = (fn: () => void) => {
    React.useEffect(() => {
        return fn;
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
};

export interface PlacementEditor {
    getContent: () => string;
    insertEntityAtEnd: (
        entityType: string,
        entityId: string,
        htmlContent: HTMLElement,
    ) => void;
    removeEntitiesByType: (entityType: string) => void;
    isReady: () => boolean;
    getPendingImageCount: () => number;
}

interface PastedImage {
    id: string;
    src: string;
}

interface EntityInfo {
    id?: string;
    type?: string;
}

interface EntityObserverCallbacks {
    onEntityAdded?: (entity: EntityInfo) => void;
    onEntityRemoved?: (entity: EntityInfo) => void;
}

class EntityObserver implements EditorPlugin {
    private knownEntities: Set<string> = new Set();
    private editor: EditorAdapter | null = null;

    constructor(private callbacks: EntityObserverCallbacks = {}) {}

    getName() {
        return "EntityObserver";
    }

    initialize(editor: EditorAdapter) {
        this.editor = editor;
        this.updateKnownEntities();
    }

    dispose() {
        this.knownEntities.clear();
        this.editor = null;
    }

    onPluginEvent(event: PluginEvent) {
        if (event.eventType === "contentChanged" && this.editor) {
            this.checkEntityChanges();
        }
    }

    private getEntityKey(entity: EntityInfo): string {
        return `${entity.type || "unknown"}:::${entity.id || "no-id"}`;
    }

    private getCurrentEntities(): EntityInfo[] {
        if (!this.editor) return [];

        const entities: EntityInfo[] = [];

        this.editor.formatContentModel(contentModel => {
            contentModel.blocks.forEach(block => {
                if (block.blockType === "Entity" && block.entityFormat) {
                    entities.push({
                        id: block.entityFormat.id,
                        type: block.entityFormat.entityType || block.entityFormat.id,
                    });
                }
            });
            return false;
        });

        return entities;
    }

    private updateKnownEntities() {
        this.knownEntities.clear();
        const current = this.getCurrentEntities();
        current.forEach(entity => {
            this.knownEntities.add(this.getEntityKey(entity));
        });
    }

    private checkEntityChanges() {
        const currentEntities = this.getCurrentEntities();
        const currentKeys = new Set(currentEntities.map(e => this.getEntityKey(e)));

        for (const entity of currentEntities) {
            const key = this.getEntityKey(entity);
            if (!this.knownEntities.has(key)) {
                this.callbacks.onEntityAdded?.(entity);
            }
        }

        Array.from(this.knownEntities).forEach(knownKey => {
            if (!currentKeys.has(knownKey)) {
                const [type, id] = knownKey.split(":::");
                this.callbacks.onEntityRemoved?.({
                    type: type === "unknown" ? undefined : type,
                    id: id === "no-id" ? undefined : id,
                });
            }
        });

        this.knownEntities = currentKeys;
    }
}

class ImagePasteExtractor implements EditorPlugin {
    constructor(private onImagesPasted: (images: PastedImage[]) => void) {}

    getName() {
        return "ImagePasteExtractor";
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    initialize(_editor: EditorAdapter) {}

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    dispose() {}

    private shouldHandleImage(src: string) {
        if (!src) {
            return false;
        }

        if (src.startsWith("http://") || src.startsWith("https://")) {
            return true;
        }

        if (src.startsWith("data:image/") && src.includes(";base64,")) {
            return true;
        }

        return false;
    }

    onPluginEvent(event: PluginEvent) {
        const imageUrls: PastedImage[] = [];

        if (event.eventType === "beforePaste") {
            event.fragment
                .querySelectorAll("[id]")
                .forEach(el => el.removeAttribute("id"));

            event.fragment.querySelectorAll("img").forEach(img => {
                if (this.shouldHandleImage(img.src)) {
                    const id = genInternalId();
                    img.setAttribute("data-relocate-image-id", id);
                    imageUrls.push({
                        id,
                        src: img.src,
                    });
                }
            });
        }

        if (imageUrls.length > 0) {
            this.onImagesPasted(imageUrls);
        }
    }
}

const createEditor = (
    contentDiv: HTMLDivElement,
    initialContent: string | undefined,
    onImagesPasted: (images: PastedImage[]) => void,
    entityObserverCallbacks?: EntityObserverCallbacks,
): EditorAdapter => {
    if (!contentDiv.className.includes("roosterjs")) {
        contentDiv.className += " roosterjs";
    }
    const plugins: EditorPlugin[] = [
        new PastePlugin(),
        new EditPlugin(),
        new AutoFormatPlugin(),
        new ImagePasteExtractor(onImagesPasted),
        new HyperlinkPlugin(),
        new ShortcutPlugin(),
    ];

    if (entityObserverCallbacks) {
        plugins.push(new EntityObserver(entityObserverCallbacks));
    }

    const contentWithoutIds = removeHtmlIds(initialContent);

    const options: EditorAdapterOptions = {
        plugins,
        initialContent: contentWithoutIds,
    };
    return new EditorAdapter(contentDiv, options);
};

const Editor: ForwardRefExoticComponent<Props & RefAttributes<PlacementEditor>> =
    forwardRef<PlacementEditor, Props>((props, ref) => {
        const {
            initialContent,
            templates,
            signatures,
            onTemplateInserted,
            tokenReplacements,
            onTokenInserted,
            onBlur,
            placeholderTypes,
            preserveTokens,
            onEntityAdded,
            onEntityRemoved,
        } = props;
        const responsiveMode = useResponsiveMode();
        const isMobile =
            responsiveMode === ResponsiveMode.small ||
            responsiveMode === ResponsiveMode.medium;
        const editorRef = React.useRef<EditorAdapter>();
        const update = useForceUpdate();
        const [editorError, setError] = React.useState<string | null>(null);
        const pendingImageCountRef = React.useRef<number>(0);

        const mutation = useRelocateImageMutation();

        const markImageAsFailed = React.useCallback(
            (imageId: string) => {
                if (editorRef.current && !editorRef.current.isDisposed()) {
                    const imgElement = editorRef.current
                        .getDocument()
                        .querySelector(`[data-relocate-image-id="${imageId}"]`);

                    if (imgElement) {
                        imgElement.setAttribute("src", "");
                        imgElement.setAttribute("alt", "Image failed to load");
                        imgElement.setAttribute(
                            "style",
                            `background-color: rgb(220, 38, 38); color: white;`,
                        );

                        setError(
                            "Some images in your pasted content will not display properly in your email. To correct these:\n\n1. Locate the source image in your original document or email.\n2. Right-click the image and select 'Copy image'.\n3. Paste the image into your favorite editor to size it appropriately.\n4. Paste the right-sized image here in place of the broken one.",
                        );
                    }
                }
            },
            [editorRef, setError],
        );

        const handleImagesPasted = React.useCallback(
            async (images: PastedImage[]) => {
                pendingImageCountRef.current += images.length;
                for (const image of images) {
                    try {
                        const result = await mutation.mutateAsync({
                            payload: {
                                id: image.id,
                                src: image.src,
                            },
                        });

                        if (
                            result.relocateImage?.src &&
                            editorRef.current &&
                            result.relocateImage.src !== image.src
                        ) {
                            editorRef.current
                                .getDocument()
                                .querySelector(`[data-relocate-image-id="${image.id}"]`)
                                ?.setAttribute("src", result.relocateImage.src);
                        } else {
                            markImageAsFailed(image.id);
                        }
                    } catch (e) {
                        markImageAsFailed(image.id);
                        console.warn(e);
                    } finally {
                        pendingImageCountRef.current = Math.max(
                            0,
                            pendingImageCountRef.current - 1,
                        );
                    }
                }
            },
            [editorRef, markImageAsFailed, mutation],
        );

        const entityObserverCallbacks = React.useMemo(() => {
            if (onEntityAdded || onEntityRemoved) {
                return {
                    onEntityAdded,
                    onEntityRemoved,
                };
            }
            return undefined;
        }, [onEntityAdded, onEntityRemoved]);

        const init = React.useCallback(
            (el: HTMLDivElement) => {
                if (el && !editorRef.current) {
                    el.setAttribute("style", defaultEmailStyleString);
                    editorRef.current = createEditor(
                        el,
                        initialContent,
                        handleImagesPasted,
                        entityObserverCallbacks,
                    );
                    //createEditor(el, [], initialContent);
                    update();
                }
            },
            [
                editorRef,
                handleImagesPasted,
                initialContent,
                entityObserverCallbacks,
                update,
            ],
        );

        const handleBlur = React.useCallback(() => {
            if (editorRef.current && onBlur) {
                onBlur();
            }
        }, [editorRef, onBlur]);

        useImperativeHandle(ref, () => {
            return {
                getContent: () =>
                    editorRef.current ? editorRef.current.getContent() : "",
                insertEntityAtEnd: (
                    entityType: string,
                    entityId: string,
                    htmlContent: HTMLElement,
                ) => {
                    if (editorRef.current) {
                        createEntity(htmlContent, true, undefined, entityType, entityId);

                        insertEntity(editorRef.current, entityType, true, "end", {
                            contentNode: htmlContent,
                            focusAfterEntity: false,
                        });
                    }
                },
                removeEntitiesByType: (entityType: string) => {
                    const removeExistingBlocks: ContentModelFormatter = contentModel => {
                        const originalLength = contentModel.blocks.length;
                        contentModel.blocks = contentModel.blocks.filter(block => {
                            if (
                                block.blockType === "Entity" &&
                                block.entityFormat &&
                                block.entityFormat.entityType === entityType
                            ) {
                                return false;
                            }
                            return true;
                        });
                        return contentModel.blocks.length !== originalLength;
                    };

                    if (editorRef.current) {
                        editorRef.current.formatContentModel(removeExistingBlocks);
                    }
                },
                isReady: () => !!editorRef.current,
                getPendingImageCount: () => pendingImageCountRef.current,
            };
        }, [editorRef]);
        useUnmount(() => {
            if (editorRef.current) {
                editorRef.current.dispose();
            }
        });

        const showTopRibbon =
            signatures?.length > 0 ||
            templates?.length > 0 ||
            placeholderTypes?.length > 0 ||
            !isMobile;

        return (
            <div>
                {editorRef.current && showTopRibbon ? (
                    <Ribbon position="top">
                        {isMobile ? null : (
                            <FormattingButtons editor={editorRef.current} />
                        )}
                        <TemplateMenu
                            templates={templates}
                            editor={editorRef.current}
                            onTemplateInserted={onTemplateInserted}
                            tokenReplacements={tokenReplacements}
                            onTokenInserted={onTokenInserted}
                            preserveTokens={preserveTokens}
                        />
                        <SignatureMenu
                            signatures={signatures}
                            editor={editorRef.current}
                        />
                        <PlaceholderPopover
                            editor={editorRef.current}
                            tokenReplacements={tokenReplacements}
                            onTokenInserted={onTokenInserted}
                            placeholderTypes={placeholderTypes}
                        />
                    </Ribbon>
                ) : null}
                <div
                    className={`unreset overflow-scroll h-80 border-2 border-gray-200 p-4 min-w-full`}
                    onBlur={handleBlur}
                    translate="no"
                    spellCheck={true}
                    ref={init}></div>
                {editorRef.current && isMobile ? (
                    <Ribbon position="bottom">
                        <FormattingButtons
                            editor={editorRef.current}
                            popoverAnchor="top"
                        />
                    </Ribbon>
                ) : null}
                {editorError ? (
                    <div className="mt-2">
                        <InlineBanner type="error">
                            {breakNewlines(editorError)}
                        </InlineBanner>
                    </div>
                ) : null}
            </div>
        );
    });

Editor.displayName = "Editor";

export { Editor };
export type { EntityInfo };
